/* eslint-disable @typescript-eslint/no-explicit-any */
import moment from 'moment'
import { SVGS } from '@/assets/svgs'
import {
  ConnectionModel,
  DMTopic,
  Ego,
  EgoBasicInfo,
  IPatternAssetTemplate,
  MyConnection,
  Note,
  NoteTextData,
  PatternDraft,
  PatternMeshColor,
  Topic,
  TopicCreateResponse,
  TopicStats,
} from '@/types'
import Resizer from 'react-image-file-resizer'
import {
  ANONYMOUS_PATTERN_DEFAULT_NAME,
  AVATAR_BG_COLORS,
  AVATAR_FILE_EXTENSIONS,
  isDefaultValue,
  MandatoryNoteTypes,
  NoteType,
  OddlyouNoteType,
  PrivacyType,
  TopicType,
  TopicTypes,
} from './constants'
import { Assets } from './pattern-assets'
import htmlToDraft from 'html-to-draftjs'
import { number } from 'prop-types'
import {
  ActivityTypeUtil,
  NotificationGroup,
  NotificationItem,
  SocketEventType,
} from '@/types/notification'

export const Patterns = {
  email: /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/,
  phone: /^\d{10}$/,
  // eslint-disable-next-line no-useless-escape
  password: /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{8,})/,
  firstName: /^[a-zA-Z]+|[a-zA-Z]+ [a-zA-Z]+$/,
  lastName: /^[a-zA-Z]+$/,
}

export const getCopyRightText = () => {
  return `© ${new Date().getFullYear()} ALL RIGHTS RESERVED.`
}

export const validateEmail = (emailAdress: string) => {
  // eslint-disable-next-line no-useless-escape
  const regexEmail = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/
  return emailAdress.match(regexEmail)
}

export const validatePhoneNumber = (number: string) => {
  // eslint-disable-next-line no-useless-escape
  const regexPhone = /^\d{10}$/
  return number.match(regexPhone)
}

export const validatePassword = (password: string) => {
  // eslint-disable-next-line no-useless-escape
  const regexPhone = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{8,})/
  return password.match(regexPhone)
}

export const validateFirstName = (name: string) => {
  // eslint-disable-next-line no-useless-escape
  const regexPhone = /^[a-zA-Z]+|[a-zA-Z]+ [a-zA-Z]+$/
  return name.match(regexPhone)
}

export const validateLastName = (name: string) => {
  // eslint-disable-next-line no-useless-escape
  const regexPhone = /^[a-zA-Z]+$/
  return name.match(regexPhone)
}

export const resizeFile = (file: File) =>
  new Promise((resolve) => {
    Resizer.imageFileResizer(
      file,
      1000,
      1000,
      'jpg',
      100,
      0,
      (uri: any) => {
        resolve(uri)
      },
      'file',
    )
  })

export const uploadMedia = async function (data: File) {
  try {
    return await resizeFile(data)
  } catch (err) {
    return { isError: true, errorMessage: err }
  }
}

export const getFileUniqueKey = (file: File) => {
  return `${file.lastModified}-${file.size}-${file.name}`
}

export const getTooltipTitleForPrivacyUsers = (topic: Topic | DMTopic) => {
  if (topic.visibility === '4') {
    return topic.connections.map((i) => `${i.firstName} ${i.lastName}`).join(', ')
  }
  if (topic.visibility === '5') {
    return (topic as Topic).cliques?.map((i) => i.cliqueName).join(', ') || ''
  }
  if (topic.visibility === '6') {
    return `${topic.connections[0]?.firstName} ${topic.connections[0]?.lastName}`
  }
  return ''
}

export const convertToGMT = (dateString: string) => {
  return `${dateString} GMT`
}

export const isValidAvatarUrl = (url: string) => {
  if (url.includes('profile_male')) return false
  const lowerCase = url.toLowerCase()
  if (!AVATAR_FILE_EXTENSIONS.some((i) => lowerCase.includes(i))) return false
  try {
    new URL(url)
    return true
  } catch (err) {
    return false
  }
}

export const SocialIcons = {
  like: {
    key: 'like',
    icon: SVGS.ActionLike,
    width: '15.27px',
    height: '14px',
  },
  likeFilled: {
    key: 'like-filled',
    icon: SVGS.ActionLikeFilled,
    width: '15.27px',
    height: '14px',
  },

  comment: {
    key: 'comment',
    icon: SVGS.ActionComment,
    width: '16.14px',
    height: '14.04px',
  },

  view: {
    key: 'visit',
    icon: SVGS.ActionVisit,
    width: '17.73px',
    height: '14px',
  },

  note: {
    key: 'media',
    icon: SVGS.ActionMedia,
    width: '14px',
    height: '14px',
  },
}

export const getTopicStates = (topic: Topic) => {
  const states = [
    {
      ...SocialIcons.like,
      value: Number(topic.totalLikes),
    },
    {
      ...SocialIcons.comment,
      value: Number(topic.totalComments),
    },
    {
      ...SocialIcons.view,
      value: Number(topic.visitorVisits) + Number(topic.ownerVisits),
    },
  ]

  let mediaCount = 0
  if (topic.topicType == TopicType.ALBUM.toString()) {
    // topic.notes?.forEach((note) => (mediaCount += note.egoList?.length || 0))
    mediaCount = Number(topic.noteCount) - 1
  } else {
    mediaCount = Number(topic.noteCount) - 1
  }

  states.splice(0, 0, {
    ...SocialIcons.note,
    value: mediaCount || Number(topic.photoCount),
  })
  states.push()
  return states.filter((i) => i.value > 0)
}

export const getDMTopicStates = (state: TopicStats) => {
  const states = [
    {
      key: 'like',
      icon: SVGS.ActionLike,
      value: Number(state.noteThumbsUp),
    },
    {
      key: 'comment',
      icon: SVGS.ActionComment,
      value: Number(state.noteComments),
    },
    {
      key: 'visit',
      icon: SVGS.ActionVisit,
      value: Number(state.visitorVisits) + Number(state.ownerVisits),
    },
    {
      visit: 'media',
      icon: SVGS.ActionMedia,
      value: Number(state.egoCount),
    },
  ]
  return states.filter((i) => i.value > 0)
}

export const getAvatarColorWithName = (name: string) => {
  const [firstName, lastName] = name.split(' ')
  const hash = firstName.charCodeAt(0) + (lastName || 'a').charCodeAt(0) + name.length
  return AVATAR_BG_COLORS[hash % AVATAR_BG_COLORS.length]
}

export const isPatternTopicType = (type: string | number) => {
  return type === '200' || type === 200
}

export const isGalleryTopicType = (type: string | number) => {
  return type === '201' || type === 201
}

export const isWallArtTopicType = (type: string | number) => {
  return type === '202' || type === 202
}

export const isQuiltTopicType = (type: string | number) => {
  return type === '203' || type === 203
}

export const isCustomPrintTopicType = (type: string | number) => {
  return type == TopicType.CUSTOM_PRINTS
}

export const isBabylonType = (type: string | number) => {
  return (
    isPatternTopicType(type) ||
    isQuiltTopicType(type) ||
    isGalleryTopicType(type) ||
    isWallArtTopicType(type) ||
    isCustomPrintTopicType(type)
  )
}

export const getFullImageURL = (imageURL: string) => {
  if (imageURL?.length > 0) {
    let result = imageURL?.replace('/photo.album.', '/photo.full.')
    result = result?.replace('/photo.thumb.', '/photo.full.')
    return result
  }
  return ''
}

export const getThumbImageURL = (imageURL: string) => {
  if (imageURL?.length > 0) {
    let result = imageURL?.replace('/photo.album.', '/photo.thumb.')
    result = result?.replace('/photo.full.', '/photo.thumb.')
    return result
  }
  return ''
}

export const getAssetThumb = (type: NoteType, id: number): string | undefined => {
  let result: string | undefined

  if (type == NoteType.TEMPLATE) {
    result = Assets.templates.find((e) => e.id == id)?.thumb
  } else if (type == NoteType.MATERIAL) {
    result = Assets.materials.find((e) => e.id == id)?.thumb
  } else if (type == NoteType.ACCENT) {
    result = Assets.accents.find((e) => e.id == id)?.thumb
  } else if (type == NoteType.FRAME) {
    result = Assets.frames.find((e) => e.id == id)?.thumb
  } else if (type == NoteType.PRINT) {
    result = Assets.prints.standard.find((e) => e.id == id)?.thumb
  } else if (type == NoteType.PATTERNFRAME) {
    result = Assets.patternFrames.find((e) => e.id == id)?.thumb
  }
  return result ? `/assets/patterns/${result}` : undefined
}

export const getAssetName = (type: NoteType, id: number): string | undefined => {
  let result: string | undefined

  if (type == NoteType.TEMPLATE) {
    result = Assets.templates.find((e) => e.id == id)?.name
  } else if (type == NoteType.MATERIAL) {
    result = Assets.materials.find((e) => e.id == id)?.name
  } else if (type == NoteType.ACCENT) {
    result = Assets.accents.find((e) => e.id == id)?.name
  } else if (type == NoteType.FRAME) {
    result = Assets.frames.find((e) => e.id == id)?.name
  } else if (type == NoteType.PRINT) {
    result = Assets.prints.standard.find((e) => e.id == id)?.name
  } else if (type == NoteType.PATTERNFRAME) {
    result = Assets.patternFrames.find((e) => e.id == id)?.name
  }

  return result
}

export const getPlainNoteTextFromNote = (note: Note) => {
  const { noteText = '' } = note || {}
  try {
    const parsed = JSON.parse(noteText)
    const draft = htmlToDraft(parsed.text)
    const value = draft.contentBlocks.map((block) => block.getText()).join('\n')
    return value
  } catch (error) {
    return noteText
  }
}

export const getNoteTextDataFromNote = (note: Note): NoteTextData => {
  return JSON.parse(note.noteText) as NoteTextData
}

export const getNowInUTC = () => {
  return moment().utc().format('yyyy-MM-DD HH:mm:ss')
}

export const scrollToBottom = (scrollRef: any, delay: number = 0) => {
  setTimeout(() => {
    const scrollBox = scrollRef.current
    if (scrollBox) {
      scrollBox.scrollTop = scrollBox?.scrollHeight
      console.log('Scrolling down')
    }
  }, delay)
}

export const convertMyConnectionToConnectionModel = (input: MyConnection) => {
  const model: ConnectionModel = {
    ...input,
    isAssigned: false,
    isOwner: false,
    profilePicFullURL: input.albumImgURL,
    profilePicThumbURL: input.thumbImgURL,
    profilePicURL: input.albumImgURL,
    searchType: 'user',
    userName: '',
  }
  return model
}

export const convertEgoBasicInfoToConnectionModel = (input: EgoBasicInfo) => {
  const model: ConnectionModel = {
    ...input,
    isAssigned: false,
    isOwner: false,
    profilePicFullURL: input.profilePicFullURL,
    profilePicThumbURL: input.profilePicThumbURL,
    profilePicURL: input.profileCoverURL,
    searchType: 'user',
    reqReceived: input.reqSent ? `${input.reqReceivedConnReqID}` : false,
  }
  return model
}

export const isUniqueColor = (
  swatches: PatternMeshColor[][],
  color: PatternMeshColor[],
): boolean => {
  let result = true

  for (let i = 0; i < swatches.length; i++) {
    let aSwatch = swatches[i]
    let subItemFoundCount = 0
    for (let j = 0; j < aSwatch.length; j++) {
      if (
        aSwatch[j]?.r === color[j]?.r &&
        aSwatch[j]?.g === color[j]?.g &&
        aSwatch[j]?.b === color[j]?.b
      ) {
        subItemFoundCount++
      }
    }

    if (subItemFoundCount === aSwatch.length) {
      result = false
      break
    }
  }

  return result
}

export const getColorSwatches = (patternDetails: PatternDraft): PatternMeshColor[][] => {
  let colorSwatches: PatternMeshColor[][] = []

  Object.values(patternDetails?.color || {}).forEach((e) => {
    if (Array.isArray(e) && e.length > 0) {
      if (isUniqueColor(colorSwatches, e)) {
        colorSwatches.push(e)
      }
    }
  })

  return colorSwatches
}

export const isNumeric = (value: any): boolean => {
  return !isNaN(value as number) && !isNaN(parseFloat(value))
}

export const pluralize = (prefix = '', count: any, singular: string, plural: string) => {
  return `${prefix}${formattedNumber(count)} ${count == 1 ? singular : plural}`
}

export const formattedNumber = (number: any) => {
  const num = Math.abs(Number(number))
  return num >= 1.0e9
    ? (num / 1.0e9).toFixed(0) + 'B'
    : // Six Zeroes for Millions
      num >= 1.0e6
      ? (num / 1.0e6).toFixed(0) + 'M'
      : // Three Zeroes for Thousands
        num >= 1.0e3
        ? (num / 1.0e3).toFixed(0) + 'K'
        : num
}

export const getTopicNameFromTopic = (topic: Topic): string => {
  if (!topic) return ANONYMOUS_PATTERN_DEFAULT_NAME
  let result = topic.topicName || ANONYMOUS_PATTERN_DEFAULT_NAME
  // if (topic.firstNote) result = getPlainNoteTextFromNote(topic.firstNote)
  return result
}

export const getTopicTypeLabel = (topicType: string | number): string => {
  const typeNum = Number(topicType)
  const topicInfo = TopicTypes[typeNum]
  return topicInfo?.label || 'Other'
}

export const isDebugMode = () => {
  if (typeof window === 'undefined') return false
  const { hostname } = window.location
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname.includes('localhost')
}

export const groupedNotifications = (events: NotificationItem[]): NotificationGroup[] => {
  const groupMap = new Map<string, NotificationItem[]>()

  for (const anEvent of events) {
    const identifier = ActivityTypeUtil.isTopicRelatedEvent(anEvent.eventType)
      ? ActivityTypeUtil.getTopicID(anEvent)
      : anEvent.sourceUserID
    const key = `${identifier}_${anEvent.eventType}`

    const existing = groupMap.get(key)
    if (existing) {
      existing.push(anEvent)
    } else {
      groupMap.set(key, [anEvent])
    }
  }

  const flatGroupEvents = Array.from(groupMap.values(), (groupEvents) => {
    // find the event with the max eventID instead of sorting the whole group
    const latest = groupEvents.reduce((max, current) =>
      Number(current.eventID) > Number(max.eventID) ? current : max,
    )

    const groupEventType = groupEvents[0].eventType
    return {
      ...latest,
      count: groupEventType == SocketEventType.USER_CONNECTED ? 1 : groupEvents.length,
    }
  })

  return flatGroupEvents.sort((a, b) => Number(b.eventID) - Number(a.eventID))
}

export const canLoadMore = (pagination: any) => {
  const totalPages = pagination?.totalPages || pagination?.totalPage || 0
  const currentPage = pagination?.currentPage || 0
  return totalPages > currentPage
}

export const canPublishPatternTopic = (topic: Topic) => {
  const hasAccent = topic.notes.some((e) => e.noteType == NoteType.ACCENT.toString())

  const result = MandatoryNoteTypes.filter((e) => {
    let status = false
    let note = topic.notes.find((topicNote) => topicNote.noteType == e.toString())

    if (note) {
      const draftValue = note.noteText
      status =
        !isDefaultValue(draftValue, note.noteName, hasAccent) &&
        draftValue != 'null' &&
        draftValue != '{}'
    }
    return status
  })

  return result.length == MandatoryNoteTypes.length
}

export const getTopicFromCreateTopicResponse = (
  createResponse: TopicCreateResponse,
  topicTitle: string,
  topicType: TopicType,
): Topic | undefined => {
  const nowInUTC = getNowInUTC()

  if (createResponse.isError) {
    return undefined
  }

  const firstNote: Note = {
    noteID: createResponse.noteID,
    authorID: createResponse.authorID,
    authProfileName: createResponse.authProfileName,
    authProfileThumbURL: createResponse.authProfileThumbURL,
    noteType: OddlyouNoteType.TEXT.toString(),
    isThumbsUp: 0,
    thumbsUp: '0',
    thumbsDown: '0',
    isThumbsDown: 0,
    comments: 0,
    noteName: 'Note Name Here',
    noteText: JSON.stringify({
      text: topicTitle,
      attachments: [],
    }),
    likes: [],
    backgroundID: '0',
    fontID: '0',
    scale: '1',
    color: {
      r: '0',
      g: '0',
      b: '0',
    },
    egoList: [],
    dateCreated: nowInUTC,
    dateUpdated: nowInUTC,
  }

  const newTopic: Topic = {
    cliqueTypes: ['friends', 'family'],
    cliques: [],
    connections: [],
    dateAdded: nowInUTC,
    dateCreated: nowInUTC,
    dateLastInteracted: nowInUTC,
    dateUpdated: nowInUTC,
    latestNote: firstNote,
    firstNote: firstNote,
    mediaCount: 12,
    noteCount: '0',
    latestEgo: undefined as unknown as Ego,
    notes: [firstNote],
    ownerVisits: '0',
    photos: [],
    photoCount: '0',
    profilePicThumbURL: createResponse.authProfileThumbURL,
    topicDescription: '',
    topicID: createResponse.topicID,
    topicName: topicTitle,
    topicType: topicType.toString(),
    topicXData: 'X Data Here',
    totalComments: '0',
    totalDislikes: '0',
    totalLikes: '0',
    userID: createResponse.authorID,
    userName: createResponse.authProfileName,
    visibility: PrivacyType.PRIVATE.toString(),
    visitorVisits: '0',
    pagination: {
      currentPage: 1,
      totalPages: 1,
      pageSize: 20,
      notesCount: 1,
    },
  }

  return newTopic
}

export const getNoteFromCreateNoteResponse = (
  responseData: any,
  noteTextData: NoteTextData,
  noteType: string,
): Note | null => {
  const { noteID, dateCreated, dateUpdated, authProfileName, authorID, authProfileThumbURL } =
    responseData
  const note: Note = {
    authProfileName,
    authProfileThumbURL,
    authorID,
    backgroundID: '',
    color: {
      r: '0',
      g: '0',
      b: '0',
    },
    comments: 0,
    likes: [],
    dateCreated,
    dateUpdated,
    egoList: [],
    fontID: '0',
    isThumbsDown: 0,
    isThumbsUp: 0,
    noteID,
    noteName: 'Note Name Here',
    noteText: JSON.stringify(noteTextData),
    noteType,
    scale: '0',
    thumbsDown: '0',
    thumbsUp: '0',
  }

  return note
}
